import { z } from 'zod';

/**
 * Phase 4 — Approval Center DTOs.
 *
 * Mirror of ApprovalQueueService input + ApprovalRequestProjection +
 * ApprovalDecisionProjection shapes.  Both sides stay in lockstep —
 * adding a field requires touching this file AND the service AND the
 * DB column (migration 0072).
 *
 * See docs/ai-platform/05-GOVERNANCE.md.
 */

export const ApprovalType = z.enum([
  'ai_action',
  'bulk_action',
  'client_approval',
  'budget',
  /** Phase 5 chunk 8 — gated workflow state change.  Context carries
   *  `{ taskId, fromStateId, toStateId, requestedStatusId, requesterId }`;
   *  the WorkflowTransitionApprovalListener applies the transition
   *  under the requester's RLS context once the approval lands. */
  'workflow_transition',
]);
export type ApprovalTypeValue = z.infer<typeof ApprovalType>;

export const ApprovalRequesterType = z.enum(['user', 'bot', 'system']);
export type ApprovalRequesterTypeValue = z.infer<typeof ApprovalRequesterType>;

export const ApprovalStatus = z.enum([
  'pending',
  'approved',
  'rejected',
  'changes_requested',
  'expired',
  'cancelled',
]);
export type ApprovalStatusValue = z.infer<typeof ApprovalStatus>;

/**
 * Execution is intentionally separate from the human decision.  "Approved"
 * means a person authorised the immutable revision; it does not claim the
 * side effect succeeded.  A running execution is not automatically retried:
 * after a crash its external outcome may be indeterminate unless the sink
 * honours the stable operation key.
 */
export const ApprovalExecutionStatus = z.enum([
  'not_required',
  'pending',
  'running',
  'succeeded',
  'failed',
  'indeterminate',
]);
export type ApprovalExecutionStatusValue = z.infer<
  typeof ApprovalExecutionStatus
>;

export const ApprovalDecisionVerb = z.enum([
  'approve',
  'reject',
  'request_changes',
]);
export type ApprovalDecisionVerbValue = z.infer<typeof ApprovalDecisionVerb>;

export const ApprovalPermittedAction = z.enum([
  'approve',
  'reject',
  'request_changes',
  'cancel',
  'revise',
]);
export type ApprovalPermittedActionValue = z.infer<
  typeof ApprovalPermittedAction
>;

export const ApprovalPolicy = z.enum(['any', 'all', 'majority']);
export type ApprovalPolicyValue = z.infer<typeof ApprovalPolicy>;

/**
 * One required-approver descriptor.  Resolved at decision time:
 *   - { type: 'role',  value: 'lead' }  → all users with that team role
 *   - { type: 'user',  value: '<uuid>' } → that exact user
 *
 * Service rejects an empty approvers list — a request with zero
 * approvers can never be resolved and would bloat the queue.
 */
export const RequiredApproverSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('role'),
    /** Preset role name (OWNER / MANAGER / LEAD / MEMBER / OUTSTAFF /
     *  VIEWER) or a custom-role id; service resolves both. */
    value: z.string().min(1).max(80),
  }),
  z.object({
    type: z.literal('user'),
    value: z.string().uuid(),
  }),
]);
export type RequiredApprover = z.infer<typeof RequiredApproverSchema>;

export const ApprovalActionTargetSchema = z.object({
  kind: z.enum(['task', 'page', 'project', 'github', 'agent_run']),
  id: z.string().min(1).max(300),
  label: z.string().min(1).max(500).nullable(),
});

const Uuid = z.string().uuid();
const NullableUuid = Uuid.nullable();
const AgentActionBase = {
  /** Exact normalized MCP arguments, excluding the server-forced run id. */
  args: z.record(z.unknown()),
  target: ApprovalActionTargetSchema,
};

/**
 * Exact executable action shapes.  These intentionally duplicate the material
 * fields from `args`: clients render the named fields while the executor first
 * verifies that rebuilding args from them yields the stored digest.
 */
export const AgentApprovalActionSchema = z.discriminatedUnion('tool', [
  z.object({
    ...AgentActionBase,
    tool: z.literal('task.create'),
    projectId: Uuid,
    title: z.string().min(1).max(300),
    description: z.string().max(60000).nullable(),
    taskType: z.string().min(1).max(80),
    priority: z.number().int().min(1).max(4),
    statusId: z.string().min(1).max(80).nullable(),
    assigneeUserId: NullableUuid,
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('task.change_status'),
    taskId: Uuid,
    beforeStatusId: z.string().min(1).max(80),
    requestedStatusId: z.string().min(1).max(80),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('task.set_assignee'),
    taskId: Uuid,
    beforeAssigneeUserId: NullableUuid,
    assigneeUserId: Uuid,
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('task.set_priority'),
    taskId: Uuid,
    beforePriority: z.number().int().min(1).max(4),
    priority: z.number().int().min(1).max(4),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('task.comment'),
    taskId: Uuid,
    body: z.string().min(1),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('page.create'),
    title: z.string().min(1).max(300),
    markdown: z.string(),
    projectId: NullableUuid,
    parentPageId: NullableUuid,
    destination: z.string().min(1).max(500),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('page.append'),
    pageId: Uuid,
    beforeRevision: z.string().min(1).max(200),
    markdown: z.string().min(1),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('page.resolve_folder'),
    path: z.array(z.string().min(1).max(300).refine((value) => value.trim().length > 0)).min(1).max(20),
    projectId: NullableUuid,
    destination: z.string().min(1).max(500),
  }).strict(),
  z.object({
    ...AgentActionBase,
    tool: z.literal('github.comment'),
    taskId: Uuid,
    body: z.string().min(1),
    destination: z.literal('github_links_for_task'),
  }).strict(),
]);
export type AgentApprovalAction = z.infer<typeof AgentApprovalActionSchema>;

/**
 * Immutable, server-authored preview consumed by every approval surface.
 * Raw context remains available for diagnostics, but executable listeners use
 * this typed snapshot and its digest as their authority.
 */
export const ApprovalActionSnapshotSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('workflow_transition'),
    target: ApprovalActionTargetSchema,
    requestedBy: z.string().uuid(),
    sodPrincipalId: z.string().uuid(),
    teamId: z.string().uuid(),
    transitionId: z.string().uuid(),
    requiredRolePreset: z.string().min(1).max(80).nullable(),
    requiresApproval: z.literal(true),
    edgeRequiresApproval: z.boolean(),
    before: z.object({
      workflowId: z.string().uuid(),
      stateId: z.string().uuid(),
      status: z.string().min(1).max(80),
      revision: z.string().min(1).max(200),
    }),
    after: z.object({
      workflowId: z.string().uuid(),
      stateId: z.string().uuid(),
      status: z.string().min(1).max(80),
    }),
    source: z.enum(['workflow_edge', 'policy']),
    policyId: z.string().uuid().nullable(),
  }),
  z.object({
    kind: z.literal('agent_tool'),
    teamId: z.string().uuid(),
    agentRunId: z.string().uuid(),
    requestedBy: z.string().uuid(),
    sodPrincipalId: z.string().uuid(),
    action: AgentApprovalActionSchema,
  }),
]);
export type ApprovalActionSnapshot = z.infer<
  typeof ApprovalActionSnapshotSchema
>;

export const CreateApprovalSchema = z.object({
  type: ApprovalType,
  subject: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  /** Free-form JSONB; shape depends on `type` (see migration 0072 header). */
  context: z.record(z.unknown()).optional(),
  requiredApprovers: z.array(RequiredApproverSchema).min(1).max(20),
  approvalPolicy: ApprovalPolicy.default('any'),
  /** Hard expiration in ISO-8601.  Service rejects values <= now. */
  ttlAt: z.string().datetime().optional(),
  escalationTo: z.array(RequiredApproverSchema).max(20).optional(),
  correlationId: z.string().min(1).max(200).optional(),
});
export type CreateApprovalInput = z.infer<typeof CreateApprovalSchema>;

export const DecideApprovalSchema = z.object({
  decision: ApprovalDecisionVerb,
  comment: z.string().max(2000).optional(),
});
export type DecideApprovalInput = z.infer<typeof DecideApprovalSchema>;

export const CancelApprovalSchema = z.object({
  /** Cancellation reason — surfaced in the audit trail.  Optional but
   *  strongly recommended; FE prompts for it. */
  reason: z.string().max(2000).optional(),
});
export type CancelApprovalInput = z.infer<typeof CancelApprovalSchema>;

export const ReviseApprovalSchema = z.object({
  subject: z.string().min(1).max(200),
  description: z.string().max(4000).nullable().optional(),
  context: z.record(z.unknown()).default({}),
  requiredApprovers: z.array(RequiredApproverSchema).min(1).max(20),
  approvalPolicy: ApprovalPolicy.default('any'),
  ttlAt: z.string().datetime().nullable().optional(),
});
export type ReviseApprovalInput = z.infer<typeof ReviseApprovalSchema>;

/**
 * Projection returned by ApprovalQueueService.list() / .getById().
 *
 * Excludes the hash-chain columns — those are internal to the Trust
 * Center verifier.  FE consumes this directly.
 */
export const ApprovalRequestSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  type: ApprovalType,
  requesterId: z.string().uuid().nullable(),
  requesterType: ApprovalRequesterType,
  subject: z.string(),
  description: z.string().nullable(),
  context: z.record(z.unknown()),
  requiredApprovers: z.array(RequiredApproverSchema),
  approvalPolicy: ApprovalPolicy,
  ttlAt: z.string().datetime().nullable(),
  escalationTo: z.array(RequiredApproverSchema).nullable(),
  status: ApprovalStatus,
  resolvedAt: z.string().datetime().nullable(),
  resolvedBy: z.string().uuid().nullable(),
  resolutionComment: z.string().nullable(),
  correlationId: z.string().nullable(),
  revision: z.number().int().positive(),
  supersedesApprovalId: z.string().uuid().nullable(),
  actionSnapshot: ApprovalActionSnapshotSchema.nullable(),
  actionDigest: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  sodPrincipalId: z.string().uuid().nullable(),
  sodPolicy: z.literal('requester_must_differ'),
  executionStatus: ApprovalExecutionStatus,
  executionOperationKey: z.string().nullable(),
  executionRequestDigest: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  executionClaimedAt: z.string().datetime().nullable(),
  executionFinishedAt: z.string().datetime().nullable(),
  executionOutcome: z.record(z.unknown()).nullable(),
  executionErrorCode: z.string().nullable(),
  executionErrorMessage: z.string().nullable(),
  /** Actor-specific UI hint computed from live membership/role, SoD, TTL,
   *  and request status. Mutation endpoints remain authoritative. */
  permittedActions: z.array(ApprovalPermittedAction),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ApprovalRequestProjection = z.infer<typeof ApprovalRequestSchema>;

/** Decision row projection (excludes hash chain). */
export const ApprovalDecisionSchema = z.object({
  id: z.string().uuid(),
  approvalId: z.string().uuid(),
  teamId: z.string().uuid(),
  approverId: z.string().uuid().nullable(),
  decision: ApprovalDecisionVerb,
  comment: z.string().nullable(),
  decidedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type ApprovalDecisionProjection = z.infer<typeof ApprovalDecisionSchema>;

/** List wrapper returned by GET /api/teams/:slug/approvals. */
export const ApprovalListResponseSchema = z.object({
  items: z.array(ApprovalRequestSchema),
});
export type ApprovalListResponse = z.infer<typeof ApprovalListResponseSchema>;

/** List wrapper returned by GET /api/approvals/:id/decisions. */
export const ApprovalDecisionListResponseSchema = z.object({
  items: z.array(ApprovalDecisionSchema),
});
export type ApprovalDecisionListResponse = z.infer<
  typeof ApprovalDecisionListResponseSchema
>;
